// Dialogs use real router history so Android's WebView back gesture closes the
// uppermost sheet, including when the app was opened directly on the hub.
const managers = new WeakMap()
const QUERY = "hubDialog"
let sequence = 0

function location(route, token) {
  const query = { ...route.query }
  if (token) query[QUERY] = token
  else delete query[QUERY]
  return { path: route.path, query, hash: route.hash || "" }
}

const pageKey = route => JSON.stringify([route.path, route.hash || "", Object.keys(route.query || {}).filter(key => key !== QUERY).sort().map(key => [key, route.query[key]])])

export function isHubDialogNavigation(to, from) {
  return to.query?.[QUERY] !== from.query?.[QUERY] && pageKey(to) === pageKey(from)
}

function manager(router) {
  if (managers.has(router)) return managers.get(router)
  const state = { entries: [], pending: new Set() }
  managers.set(router, state)
  router.afterEach(to => {
    const index = state.entries.findIndex(entry => entry.path === to.fullPath)
    const removed = state.entries.splice(index + 1)
    removed.reverse().forEach(entry => { entry.active = false; entry.onBack() })
    notifyTop(state)
    // Forward navigation must never resurrect a closed sheet or another account's data.
    if (index === -1 && to.query?.[QUERY]) track(state, router.replace(location(to)).catch(() => {}))
  })
  return state
}

function notifyTop(state) {
  const top = state.entries[state.entries.length - 1]
  state.entries.forEach(entry => entry.onTop(entry === top))
}

function track(state, promise) {
  state.pending.add(promise)
  promise.finally(() => state.pending.delete(promise))
  return promise
}

export async function hubDialogsSettled(router) {
  const state = managers.get(router)
  while (state?.pending.size) await Promise.all([...state.pending])
}

export function openHubDialog(router, onBack, onTop = () => {}) {
  if (!router) return { close: () => Promise.resolve() }
  const state = manager(router)
  const page = pageKey(router.currentRoute), token = `sheet-${++sequence}`
  const entry = { path: "", onBack, onTop, active: true }
  // Serialize opening/closing animations that update history in the same tick.
  // Register only the push in progress, so a parent's navigation cannot close
  // a child whose history entry has not been created yet.
  track(state, Promise.all([...state.pending]).then(() => {
    if (page !== pageKey(router.currentRoute)) { entry.active = false; onBack(); return }
    const target = location(router.currentRoute, token)
    entry.path = router.resolve(target).route.fullPath
    state.entries.push(entry)
    notifyTop(state)
    return router.push(target).catch(() => {
      if (!entry.active) return
      entry.active = false
      state.entries = state.entries.filter(item => item !== entry)
      notifyTop(state)
      onBack()
    })
  }))
  return {
    close() {
      if (!entry.active) return Promise.resolve()
      return track(state, Promise.all([...state.pending]).then(() => {
        if (!entry.active) return
        const index = state.entries.indexOf(entry), top = state.entries[state.entries.length - 1]
        if (index < 0) return
        const removed = state.entries.splice(index)
        removed.forEach(item => { item.active = false })
        removed.filter(item => item !== entry).reverse().forEach(item => item.onBack())
        notifyTop(state)
        if (router.currentRoute.fullPath !== top.path) return
        return new Promise(resolve => {
          let timer
          const stop = router.afterEach(() => { stop(); clearTimeout(timer); resolve() })
          // A blocked navigation must not leave a subsequent open waiting forever.
          timer = setTimeout(() => { stop(); resolve() }, 1200)
          router.go(-removed.length)
        })
      }))
    },
  }
}
